import { ReactNode, Component, PureComponent, ReactElement } from 'react';
import _ from 'lodash';
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { getZDepthShadow } from "@Utils/Colors";
import { InfoTipLayout, AdvancedInfoTip, InfoTipItem, createPortStateFromInfoTipEvent, renderInfoTip } from "@Primitive/InfoTips";
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { ReduxThingState, StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import { WebNode } from '@API/WebNode';
import { WebThingPort, WebThing } from '@API/WebThing';
import { IWidgetSettingsProps, WidgetDescriptionToPlegmaThing, IWidgetDescription } from '@UI/Widgets/WidgetStructs';
import { SettingsList } from '@Primitive/SettingsList';
import { isFunction, isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { Plegma } from '@API/Api';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { StateManager } from "@Utils/StateManager";

// todo: add schema validator!
// =================================================================================================
const enum AdvanceInfoGridPortKeys {
    Data = "data",
    onDataChange = "on_data_change",
    FormData = "formData",
    onDropdownMenuChange = 'onDropdownMenuChange',
    onButtonElementClick = 'onButtonElementClick'
}
// -------------------------------------------------------------------------------------------------
export interface AdvanceInfoGridData {
    /**
     * The width pixels
     */
    TileWidth: number;

    /**
     * The number of columns
     */
    NumberOfColumn: number;

    /**
     * If enable the grid is define the tile width based on number of column;
     */
    UseStaticColumns: boolean;

    /**
     * The margin of the tiles in the grid
     */
    TileMargin: { w: number, h: number }

    Layout: InfoTipLayout;
    BigIconSize: number;
    FontSize: number;
    FontColor: string;
    BackgroundColor: string;
    zDepth: number;
    BorderRadius: number;
    FillHeight: boolean;
    OpenIcon?: string;
    CloseIcon?: string;
    AlignLeft?: boolean;
}
// -------------------------------------------------------------------------------------------------
export interface AdvanceInfoGridInputData extends AdvancedInfoTip {
    itemKey: string;
    itemType: "infotip" | "dynamic-form";
    formElements: SettingsGroup<any>[];
}
// -------------------------------------------------------------------------------------------------
export interface AdvanceInfoGridState {
    Data: AdvanceInfoGridInputData[];
    FormData: { [key: string]: any };
}
// -------------------------------------------------------------------------------------------------
export class AdvanceInfoGrid extends Component<WidgetsThingsStructs.PartialIWidgetThingsProps<AdvanceInfoGridData>, AdvanceInfoGridState> {
    // ---------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<AdvanceInfoGridData>) {
        super(props);

        this.state = {
            Data: [],
            FormData: {}
        };
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidMount() {
        if (_.has(this.props.PortsStates, AdvanceInfoGridPortKeys.Data)) {
            let port = this.props.PortsStates[AdvanceInfoGridPortKeys.Data];
            if (port.LastStateStatus == StateStatus.Valid)
                this.setState({ Data: port.State });
        }

        if (_.has(this.props.PortsStates, AdvanceInfoGridPortKeys.FormData)) {
            let port = this.props.PortsStates[AdvanceInfoGridPortKeys.FormData];
            if (port.LastStateStatus == StateStatus.Valid)
                this.setState({ FormData: port.State });
        }

        // Link update port
        let stateManager = StateManager.GetInstanceKey(this.props.NodeKey);
        if (isDefined(stateManager))
            stateManager.RegisterCB(this.props.ThingKey, this.stateUpdate);
    }
    // ---------------------------------------------------------------------------------------------------
    componentWillUnmount() {
        let stateManager = StateManager.GetInstanceKey(this.props.NodeKey);
        if (isDefined(stateManager))
            stateManager.UnRegisterCB(this.props.ThingKey, this.stateUpdate);
    }
    // ---------------------------------------------------------------------------------------------
    stateUpdate = (state: ReduxThingState, forceUpdate: boolean, changedSubKeys: string[]) => {
        for (const portKey of changedSubKeys) {
            let port = state.PortStates[portKey];
            if (port != null)
                this.UpdatePort(portKey, port.State, port.LastStateStatus == StateStatus.Valid);
        }
    }
    // ---------------------------------------------------------------------------------------------
    public UpdatePort(portKey: string, portState: any, isValid: boolean) {
        if (portKey == AdvanceInfoGridPortKeys.Data && isValid)
            this.setState({ Data: portState });
        if (portKey == AdvanceInfoGridPortKeys.FormData && isValid)
            this.setState({ FormData: portState });
    }
    // ---------------------------------------------------------------------------------------------
    handlePopupDataChange = (i: InfoTipItem, rerender: boolean, infoTip: AdvancedInfoTip, iconClicked: boolean) => {
        this.props.SendPortEvent(AdvanceInfoGridPortKeys.onDataChange,
            createPortStateFromInfoTipEvent(i, infoTip, iconClicked));
    }
    // ------------------------------------------------------------------------------------------------
    onDropDownMenuChanged = (key: string, dataPath: string, data: any, prevData: any, formData: any) => {
        this.props.SendPortEvent(AdvanceInfoGridPortKeys.onDropdownMenuChange
            , JSON.stringify({
                key: key,
                jsonName: dataPath,
                elementData: data,
                previousData: prevData,
                formData: formData
            }))
    }
    // ------------------------------------------------------------------------------------------------
    onButtonPressed = (key, element, data) => {
        this.props.SendPortEvent(AdvanceInfoGridPortKeys.onButtonElementClick, {
            key: key,
            element: element,
            data: data
        });
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        let tileWidth = 0;
        if (this.props.WidgetData.UseStaticColumns) {
            tileWidth = Math.floor(this.props.width / this.props.WidgetData.NumberOfColumn) - this.props.WidgetData.TileMargin.w;
        } else
            tileWidth = Math.min(this.props.width, (this.props.WidgetData.TileWidth + this.props.WidgetData.TileMargin.w));

        // TODO: define a better way to handling this :) 
        let numberOfGridColumn = 4;
        if (this.props.width < 400)
            numberOfGridColumn = 1;
        else if (this.props.width < 700)
            numberOfGridColumn = 2;

        return <div
            key={"row-grid"}
            style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill,minmax(" + tileWidth + "px, 1fr))",
                width: this.props.width,
                clear: "both",
                height: (this.props.WidgetData.FillHeight) ? "100%" : undefined,
                alignItems: (this.props.WidgetData.FillHeight) ? "center" : undefined,
                gridRowGap: this.props.WidgetData.TileMargin.h,
                gridColumnGap: this.props.WidgetData.TileMargin.w
            }}
        >
            {
                _.map(this.state.Data, (d: AdvanceInfoGridInputData, key) => {
                    if (d == null)
                        return;
                    if (d.itemType == null || d.itemType == "infotip")
                        return <div
                            key={key}
                            style={{
                                height: (this.props.WidgetData.FillHeight) ? undefined : "100%",
                            }}>
                            {renderInfoTip("info-" + key,
                                tileWidth,
                                d,
                                {
                                    Layout: this.props.WidgetData.Layout,
                                    BigIconSize: this.props.WidgetData.BigIconSize,
                                    FontSize: this.props.WidgetData.FontSize,
                                    FontColor: this.props.WidgetData.FontColor,
                                    BackgroundColor: this.props.WidgetData.BackgroundColor,
                                    BorderRadius: this.props.WidgetData.BorderRadius,
                                    groupOpenIcon: this.props.WidgetData.OpenIcon,
                                    groupCloseIcon: this.props.WidgetData.CloseIcon,
                                    groupAlignLeft: this.props.WidgetData.AlignLeft,
                                    cardStyle: {
                                        boxShadow: getZDepthShadow(this.props.WidgetData.zDepth),
                                        height: "100%"
                                    }
                                },
                                this.handlePopupDataChange)}
                        </div>

                    if (d.itemType == "dynamic-form")
                        return <div
                            key={key}
                            style={{
                                height: (this.props.WidgetData.FillHeight) ? undefined : "100%",
                                backgroundColor: this.props.WidgetData.BackgroundColor,
                                color: this.props.WidgetData.FontColor,
                                boxShadow: getZDepthShadow(this.props.WidgetData.zDepth),
                                fontSize: this.props.WidgetData.FontSize,
                                borderRadius: this.props.WidgetData.BorderRadius,
                                padding: 5
                            }}>
                            <SettingsList
                                Data={{}}
                                SettingsMap={d.formElements}
                                onElementChanged={(dataPath: string, data: any, prevData: any, formData: any) => {
                                    this.onDropDownMenuChanged(d.itemKey, dataPath, data, prevData, formData);
                                }}
                                onButtonPressed={(buttonElement, data) => {
                                    this.onButtonPressed(d.itemKey, buttonElement, data);
                                }}
                                onSaveData={_.noop}
                                AutoSave={true}
                                numberOfGridColumn={numberOfGridColumn}
                            />
                        </div>
                })
            }
        </div>;
    }
}
// =================================================================================================
class AdvanceInfoGridSettings extends PureComponent<IWidgetSettingsProps<AdvanceInfoGridData>> {
    constructor(props: IWidgetSettingsProps<AdvanceInfoGridData>) {
        super(props);
    }
    // ------------------------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<AdvanceInfoGridData>[] = [

            {
                name: 'Group Settings',
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: "Align left/right",
                        tooltip: 'Choose if your icon will be aligned left/right',
                        dataPath: "AlignLeft",
                    },
                    {
                        type: SettingsType.Icon,
                        label: "Open Icon",
                        tooltip: 'Choose an icon for opened group.',
                        dataPath: "OpenIcon",
                    },
                    {
                        type: SettingsType.Icon,
                        label: "Close Icon",
                        tooltip: 'Choose an icon for closed group.',
                        dataPath: "CloseIcon",
                    },
                ]
            }, {
                name: "",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: "Use Static #Column",
                        dataPath: "UseStaticColumns"
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Tile Width',
                        dataPath: "TileWidth",
                        tooltip: "Tile's width in pixels.",
                        minValue: 70,
                        maxValue: 600,
                        stepValue: 10,
                        valuePosfix: "px",
                        isHidden: (data) => data.UseStaticColumns
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Number of Column',
                        dataPath: "NumberOfColumn",
                        tooltip: "Dynamic selection of tile width based on column",
                        minValue: 1,
                        maxValue: 20,
                        stepValue: 1,
                        valuePosfix: "Col",
                        isHidden: (data) => !data.UseStaticColumns
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Tile Margin Width',
                        dataPath: "TileMargin.w",
                        tooltip: 'Horizontal margin in pixels between tiles.',
                        minValue: 0,
                        maxValue: 10,
                        stepValue: 1,
                        valuePosfix: "px"
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Tile Margin Height',
                        tooltip: 'Vertical margin in pixels between tiles.',
                        dataPath: "TileMargin.h",
                        minValue: 0,
                        maxValue: 10,
                        stepValue: 1,
                        valuePosfix: "px"
                    },
                    {
                        type: SettingsType.DropDown,
                        label: 'Layout',
                        tooltip: "Select tile's layout.",
                        dataPath: "Layout",
                        items: [
                            { label: "Default", value: InfoTipLayout.Default },
                            { label: "Big-Icon", value: InfoTipLayout.BigIcon }
                        ]
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Big Icon Size",
                        tooltip: 'If big-icon layout is selected, define the size of it.',
                        dataPath: "BigIconSize",
                        minValue: 0.1,
                        maxValue: 1,
                        stepValue: 0.05,
                        valuePosfix: "* Tile's Width",
                        isHidden: (data: AdvanceInfoGridData) => data.Layout == InfoTipLayout.Default
                    },
                    {
                        type: SettingsType.Number,
                        label: 'Font Size',
                        tooltip: "Tile's font size.",
                        dataPath: "FontSize",
                        oneLine: true
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Font Color',
                        tooltip: "Tile's default font color. Can be overriden from port.",
                        dataPath: "FontColor"
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Background Color',
                        tooltip: "Tile's default background color. Can be overriden from port.",
                        dataPath: "BackgroundColor"
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Shadow zDepth',
                        tooltip: "Draws shadow on the background",
                        dataPath: "zDepth",
                        minValue: 0,
                        maxValue: 6,
                        stepValue: 1,
                        valuePosfix: ""
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Border Radius',
                        tooltip: 'Sets the border radius of InfoTip item',
                        dataPath: 'BorderRadius',
                        minValue: 0,
                        maxValue: 20,
                        stepValue: 1,
                        valuePosfix: 'px'
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Fill Height',
                        tooltip: "Fills the widget's available space.",
                        dataPath: "FillHeight"
                    },
                ]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isFunction(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
}
// =================================================================================================

export class AdvanceInfoGridWidget extends IWidgetThingsClass<AdvanceInfoGridData> {
    // ------------------------------------------------------------------------------------------------------------
    Render(key: string, width: number, height: number): ReactNode {
        return <AdvanceInfoGrid />;
    }
    // ------------------------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <AdvanceInfoGridSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------------------
    private createThingPorts(): WebThingPort[] {
        let ports: WebThingPort[] = [];

        ports.push(new WebThingPort({
            Name: 'Data',
            PortKey: AdvanceInfoGridPortKeys.Data,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Input,
            State: ''
        }, WebThingPortType.Data));

        ports.push(new WebThingPort({
            Name: 'onDataChange',
            PortKey: AdvanceInfoGridPortKeys.onDataChange,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Output,
            State: ''
        }, WebThingPortType.Trigger));

        ports.push(new WebThingPort({
            Name: 'FormData',
            PortKey: AdvanceInfoGridPortKeys.FormData,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Input,
            State: ''
        }, WebThingPortType.Data));

        ports.push(new WebThingPort({
            Name: 'onDropdownMenuChange',
            PortKey: AdvanceInfoGridPortKeys.onDropdownMenuChange,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Output,
            State: ''
        }, WebThingPortType.Trigger));

        ports.push(new WebThingPort({
            Name: 'onButtonElementClick',
            PortKey: AdvanceInfoGridPortKeys.onButtonElementClick,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Output,
            State: ''
        }, WebThingPortType.Trigger));

        return ports;
    }
    // ------------------------------------------------------------------------------------------------------------
    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        // Add thing's ports.
        webThing.Ports = {};
        this.createThingPorts().forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode.GetModule(this.WebModuleKey).UpdateThing(webThing);
    }
    // ------------------------------------------------------------------------------------------------------------
    initThing() {
        let // Create plegma thing.
            plegmaThing = WidgetDescriptionToPlegmaThing(this.Description),

            // Create thing's ports and web thing.
            ports = this.createThingPorts(),
            webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): AdvanceInfoGridData {
        return {
            UseStaticColumns: false,
            NumberOfColumn: 3,
            TileWidth: 250,
            TileMargin: { w: 4, h: 4 },
            Layout: InfoTipLayout.Default,
            BigIconSize: 0.75,
            FontSize: 14,
            FontColor: "white",
            BackgroundColor: "#4A90E2",
            zDepth: 1,
            FillHeight: false,
            BorderRadius: 2,
            OpenIcon: 'far fa-angle-down',
            CloseIcon: 'far fa-angle-left',
            AlignLeft: false
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return AdvanceInfoGridWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        return {
            Title: 'Advanced Info Grid',
            WidgetTitle: '',
            Key: 'AdvanceInfoGridWidget',
            Type: 'AdvanceInfoGridWidget',
            Data: AdvanceInfoGridWidget.GetDefaultWidgetDataStatic(),
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/png/advance_info_grid.png'
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            return {
                [WidgetsThingsStructs.WidgetLocalizationKeys.Title]: this.Description.WidgetTitle
            };
        else
            // nothing to expose
            return {};
    };
    // ------------------------------------------------------------------------------------------------------------
}